import { type AttachmentType, type FileType } from "@/api";
import { DocumentViewerPanel } from "@/components/frontend/chat/DocumentViewerPanel";
import { clearChatReadyToast } from "@/components/frontend/chat/chatReadyToastRegistry";
import { Button } from "@/components/ui/button";
import {
    ResizableHandle,
    ResizablePanel,
    ResizablePanelGroup,
} from "@/components/ui/resizable";
import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,
} from "@/components/ui/sheet";
import { useOrganization } from "@/contexts/frontend/organization/useOrganization";
import { MarkdownActionsBridge } from "@/pages/frontend/chat/components/MarkdownActionsProvider";
import {
    useSheetMode,
    setActiveSheetThreadId,
} from "@/pages/frontend/chat/components/runtime-provider.sheet-mode";
import {
    StaleThreadSwitchError,
    useRequestThread,
} from "@/pages/frontend/chat/components/runtime-provider.thread-target";
import { DocumentViewerProvider } from "@/pages/frontend/chat/contexts/DocumentViewerProvider";
import { useDocumentViewer } from "@/pages/frontend/chat/contexts/useDocumentViewer";
import { setNextClientId } from "@/pages/frontend/chat/adapters/api-thread-list-adapter";
import { registerAttachmentFileType } from "@/components/shared/attachment-file-type-registry";
import { useAui } from "@assistant-ui/react";
import { Maximize2, XIcon } from "lucide-react";
import { useCallback, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { ChatSheetSurfaceContext } from "./chat-sheet-surface";
import { getThreadOwner, setThreadOwner } from "./chat-sheet.thread-ownership";
import { Thread } from "./thread";

export interface ChatSheetAttachment {
    id: number;
    name: string;
    type: AttachmentType;
    is_knowledge_base?: boolean;
    /** Underlying document's file type, so the sheet's composer pill shows a
     * spreadsheet (green) vs a PDF (red). Only meaningful for Document
     * attachments; absent/null is treated as a PDF. */
    file_type?: FileType | null;
}

export interface ChatSheetProps {
    open: boolean;
    onClose: () => void;
    /**
     * Single owned attachment. Sets owned mode: derives `ownerKey` from
     * `${type}:${id}` and persists `?threadId=…` so the same entity resumes
     * its thread on next open.
     */
    attachment?: ChatSheetAttachment;
    /**
     * Multiple ad-hoc attachments (library "Ask Polly"). No URL persistence,
     * no ownership; every open is a fresh thread.
     */
    attachments?: ChatSheetAttachment[];
    /** Owned mode with a manually-provided ownerKey. */
    ownerKey?: string;
    /**
     * Client-scoped sheet (Unit 3.3): while open, the next conversation
     * created is tagged with this client. Staged on the adapter
     * side-channel and cleared on close so it never bleeds into the
     * global chat.
     */
    clientId?: number;
}

export function ChatSheet({
    open,
    onClose,
    attachment,
    attachments,
    ownerKey,
    clientId,
}: ChatSheetProps) {
    const { settings } = useOrganization();
    const aui = useAui();
    const setSheetMode = useSheetMode();
    const requestThread = useRequestThread();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    const resolvedAttachments = attachment ? [attachment] : (attachments ?? []);
    const resolvedOwnerKey =
        ownerKey ??
        (attachment ? `${attachment.type}:${attachment.id}` : undefined);

    const isOwned = resolvedOwnerKey != null;
    const threadIdFromUrl = searchParams.get("threadId");

    const trustedThreadId: string | null =
        isOwned &&
        threadIdFromUrl &&
        getThreadOwner(threadIdFromUrl) === resolvedOwnerKey
            ? threadIdFromUrl
            : null;

    const activeThreadIdRef = useRef<string | null>(null);

    // True only during handleExpand's onClose→navigate sequence, so the
    // close/unmount effect below can skip the clear for that path.
    const isExpandingRef = useRef(false);
    // True only when this open cycle created/reused a pending thread.
    // activeThreadIdRef is also null for a reopened EXISTING thread
    // (switchToThread skips initialize(), so the sheet-mode callback never
    // fires) — that alone isn't a reliable "never sent" signal.
    const createdPendingThreadRef = useRef(false);

    const setThreadIdInUrl = useCallback(
        (id: string | null) => {
            const next = new URLSearchParams(window.location.search);
            if (id) next.set("threadId", id);
            else next.delete("threadId");
            setSearchParams(next, { replace: true });
        },
        [setSearchParams]
    );

    useEffect(() => {
        if (!isOwned || !threadIdFromUrl) return;
        if (getThreadOwner(threadIdFromUrl) === resolvedOwnerKey) return;
        setThreadIdInUrl(null);
    }, [isOwned, resolvedOwnerKey, threadIdFromUrl, setThreadIdInUrl]);

    useEffect(() => {
        if (open && trustedThreadId) clearChatReadyToast(trustedThreadId);
    }, [open, trustedThreadId]);

    // Stage the client for the adapter's initialize() while the sheet is
    // open; the cleanup clears it so an un-sent sheet can't tag the next
    // conversation started elsewhere. One tagged conversation per open:
    // the first send's initialize() consumes-and-clears, and the sheet
    // has no in-sheet new-thread affordance (switchToNewThread runs on
    // open only). Invariant (4.3.4 REVIEW): every writer of the
    // side-channel stores a concrete id or undefined, so effect ordering
    // may only ever produce NO tag, never a WRONG one.
    useEffect(() => {
        if (!open || clientId == null) return;
        setNextClientId(clientId);
        return () => setNextClientId(undefined);
    }, [open, clientId]);

    useEffect(() => {
        if (!open) return;
        const callback = (remoteId: string) => {
            activeThreadIdRef.current = remoteId;
            // Publish to the module-level store so the toast gate can suppress
            // completion notifications while this sheet is displaying the thread.
            setActiveSheetThreadId(remoteId);
            if (isOwned && resolvedOwnerKey) {
                setThreadOwner(remoteId, resolvedOwnerKey);
                setThreadIdInUrl(remoteId);
            }
        };
        setSheetMode(callback);
        // Do NOT switchToNewThread on cleanup; expand-to-full-chat unmounts
        // this component while the composer pill must survive the navigation.
        return () => {
            setSheetMode(null);
        };
    }, [open, isOwned, resolvedOwnerKey, setSheetMode, setThreadIdInUrl]);

    // Clear the store only on close/unmount, in its own effect: the one above
    // re-runs on dependency churn (setThreadIdInUrl changes identity with the
    // URL), and clearing there would drop suppression for the thread still on
    // screen until the next initialize.
    useEffect(() => {
        if (!open) return;
        return () => setActiveSheetThreadId(null);
    }, [open]);

    useEffect(() => {
        if (!open) return;
        return () => requestThread(null);
    }, [open, requestThread]);

    useEffect(() => {
        if (!open) return;
        if (!settings.ai_chat_enabled) return;
        createdPendingThreadRef.current = !trustedThreadId;
        let cancelled = false;
        (async () => {
            try {
                if (trustedThreadId) {
                    requestThread(trustedThreadId);
                    await aui.threads().switchToThread(trustedThreadId);
                } else {
                    requestThread(null);
                    await aui.threads().switchToNewThread();
                    if (cancelled) return;
                    queueMicrotask(() => {
                        if (cancelled) return;
                        const composer = aui.thread().composer();
                        // AUI's `switchToNewThread` reuses a pending "new"
                        // thread if no message was sent in it, so a prior
                        // session's composer attachments survive remounts.
                        composer.clearAttachments();
                        for (const a of resolvedAttachments) {
                            const auiId = `${a.type}:${a.id}`;
                            // Carry file_type on the side-channel keyed by the
                            // aui id so the composer pill renders a spreadsheet
                            // (green) vs a PDF (red); CompleteAttachment can't
                            // hold it.
                            registerAttachmentFileType(auiId, a.file_type);
                            composer.addAttachment({
                                id: auiId,
                                type: a.type,
                                name: a.name,
                                content: [],
                                ...(a.is_knowledge_base
                                    ? { contentType: "kb" as const }
                                    : {}),
                            });
                        }
                    });
                }
            } catch (err) {
                if (cancelled || err instanceof StaleThreadSwitchError) return;
                console.warn(
                    "[ChatSheet] thread switch failed, recovering",
                    err
                );
                if (isOwned) setThreadIdInUrl(null);
            }
        })();
        return () => {
            cancelled = true;
        };
        // Hydration is identity-driven (ownerKey + trustedThreadId); content
        // changes must not re-init. Ad-hoc callers re-mount via a React `key`.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        open,
        trustedThreadId,
        resolvedOwnerKey,
        isOwned,
        settings.ai_chat_enabled,
    ]);

    // Discards a leaked pending-thread attachment on close or unmount,
    // unless expanding to full chat. `aui` is intentionally excluded from
    // deps (matches the hydration effect above) so this only fires on an
    // open→close transition or unmount.
    useEffect(() => {
        if (!open) return;
        return () => {
            if (isExpandingRef.current) {
                isExpandingRef.current = false;
                return;
            }
            if (createdPendingThreadRef.current && !activeThreadIdRef.current) {
                aui.thread().composer().clearAttachments();
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const handleExpand = () => {
        const threadId = isOwned ? trustedThreadId : activeThreadIdRef.current;
        isExpandingRef.current = true;
        // Route through onClose first: Sheet's onOpenChange never fires on
        // route-away unmount, so consumers' close-side-effects (e.g.
        // ClientDetail's conversations invalidation) would be skipped and a
        // sheet-created conversation could be missing on back-navigation.
        onClose();
        navigate(
            threadId ? `/organizations/chat/${threadId}` : "/organizations/chat"
        );
    };

    if (!settings.ai_chat_enabled) return null;

    const headerTitle =
        (isOwned ? resolvedAttachments[0]?.name : undefined) ?? "Ask Polly";

    return (
        <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
            <SheetContent
                side="right"
                showCloseButton={false}
                className="flex flex-col gap-0 p-0 data-[side=right]:sm:max-w-[500px]"
            >
                <SheetHeader className="flex-row items-center justify-between gap-3 border-b p-4">
                    <SheetTitle className="truncate">{headerTitle}</SheetTitle>
                    <div className="flex items-center gap-1">
                        <Button
                            variant="ghost"
                            size="icon-sm"
                            onClick={handleExpand}
                            aria-label="Expand chat to full view"
                        >
                            <Maximize2 />
                        </Button>
                        <Button
                            variant="ghost"
                            size="icon-sm"
                            onClick={onClose}
                            aria-label="Close chat"
                        >
                            <XIcon />
                        </Button>
                    </div>
                </SheetHeader>
                <div className="min-h-0 flex-1 overflow-hidden">
                    {/* Marks this Thread as sheet-hosted so the composer's
                        client selector stays out of the sheet's pre-staged
                        client scope (see chat-sheet-surface.ts). */}
                    <ChatSheetSurfaceContext.Provider value={true}>
                        <DocumentViewerProvider>
                            <MarkdownActionsBridge>
                                <ChatSheetViewerLayout />
                            </MarkdownActionsBridge>
                        </DocumentViewerProvider>
                    </ChatSheetSurfaceContext.Provider>
                </div>
            </SheetContent>
        </Sheet>
    );
}

function ChatSheetViewerLayout() {
    const { isOpen } = useDocumentViewer();

    return (
        <ResizablePanelGroup orientation="vertical" className="h-full">
            {isOpen && (
                <>
                    <ResizablePanel
                        id="sheet-viewer-panel"
                        defaultSize="40%"
                        minSize="20%"
                    >
                        <DocumentViewerPanel />
                    </ResizablePanel>
                    <ResizableHandle withHandle />
                </>
            )}
            <ResizablePanel
                id="sheet-chat-panel"
                defaultSize={isOpen ? "60%" : "100%"}
                minSize="20%"
                className="flex flex-col overflow-hidden"
            >
                <Thread />
            </ResizablePanel>
        </ResizablePanelGroup>
    );
}
